import type { GameForSelect } from '~stores/games';
import type { VisiblePromotionRow } from '~stores/promotion';
import {
    ApplicationTargetEnum,
    CIRCLE_K_PROVINCES,
} from '~types/app-selection';
import type { AppTarget } from '~types/app-selection';

type ProvinceLike = {
    province?: {
        name?: string | null;
    } | null;
};

const normalizeProvinceCode = (value: string): string =>
    value.trim().toUpperCase();

const toProvinceCodeSet = (
    provinces: ProvinceLike[] | null | undefined
): Set<string> => {
    const codes = provinces
        ?.map((province) => province.province?.name)
        .filter((code): code is string => typeof code === 'string')
        .map(normalizeProvinceCode);

    return new Set(codes);
};

const hasExactCircleKProvinceSet = (provinceCodeSet: Set<string>): boolean => {
    if (provinceCodeSet.size !== CIRCLE_K_PROVINCES.length) {
        return false;
    }

    return CIRCLE_K_PROVINCES.every((province) =>
        provinceCodeSet.has(province.toUpperCase())
    );
};

export const isEligibleProvinceSetByAppTarget = (
    provinceCodeSet: Set<string>,
    appTarget: AppTarget | undefined
): boolean => {
    if (provinceCodeSet.size === 0) {
        return false;
    }

    if (appTarget === ApplicationTargetEnum.QC) {
        return provinceCodeSet.has(ApplicationTargetEnum.QC);
    }

    if (appTarget === ApplicationTargetEnum.CA) {
        return hasExactCircleKProvinceSet(provinceCodeSet);
    }

    return true;
};

export const isPromotionEligibleForFeaturedCreate = (
    promotion: VisiblePromotionRow,
    appTarget: AppTarget | undefined
): boolean => {
    const provinceCodeSet = toProvinceCodeSet(promotion.promotionProvinces);

    if (appTarget === undefined) {
        if (provinceCodeSet.size === 0) {
            return false;
        }

        return (
            hasExactCircleKProvinceSet(provinceCodeSet) ||
            provinceCodeSet.has(ApplicationTargetEnum.QC)
        );
    }

    return isEligibleProvinceSetByAppTarget(provinceCodeSet, appTarget);
};

export const isGameEligibleForFeaturedCreate = (
    game: GameForSelect,
    appTarget: AppTarget | undefined
): boolean =>
    isEligibleProvinceSetByAppTarget(
        toProvinceCodeSet(game.provinces),
        appTarget
    );
